import { inject, Provider } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { marker as T } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import {
  TN_FORM_FIELD_DISMISSIBLE_ERRORS,
  TN_FORM_FIELD_ERRORS,
  type TnFormFieldErrorResolver,
} from '@truenas/ui-components';
import { DefaultValidationError } from 'app/enums/default-validation-error.enum';
import {
  ixManualValidateErrorKey, manualValidateErrorKey, manualValidateErrorMsgKey,
} from 'app/modules/forms/ix-forms/manual-validate-error.constants';

type ErrorDetail = Record<string, unknown>;

function detail(errors: ValidationErrors, key: string): ErrorDetail {
  const value = errors[key];
  return (value && typeof value === 'object' ? value : {}) as ErrorDetail;
}

/**
 * The active key for a server-side validation failure is the boolean
 * `manualValidateError`, but the human-readable text lives in its sibling
 * keys — so the resolver must read across the set rather than off the active
 * key's value.
 */
const manualValidateKeys = new Set<string>([
  manualValidateErrorKey, manualValidateErrorMsgKey, ixManualValidateErrorKey,
]);

/**
 * Pulls the server-supplied message out of a manual validation error. Mirrors
 * the legacy `ix-errors` component, which renders `ixManualValidateError.message`
 * directly. Returns `null` when no manual message is present.
 */
function manualValidationMessage(errors: ValidationErrors | null | undefined): string | null {
  if (!errors) {
    return null;
  }
  const fromObject = (errors[ixManualValidateErrorKey] as { message?: unknown } | undefined)?.message;
  if (typeof fromObject === 'string' && fromObject.trim()) {
    return fromObject;
  }
  const fromMsg = errors[manualValidateErrorMsgKey];
  if (typeof fromMsg === 'string' && fromMsg.trim()) {
    return fromMsg;
  }
  return null;
}

/**
 * Builds a translated message for a single validation error, mirroring the
 * `ix-errors` component so `tn-form-field` surfaces the same wording and i18n.
 *
 * Returns `null` for keys we do not handle so the library falls through to its
 * built-in defaults and then the raw error key.
 */
function translateError(
  translate: TranslateService,
  key: string,
  errors: ValidationErrors,
): string | null {
  switch (key as DefaultValidationError) {
    case DefaultValidationError.Min:
      return translate.instant('Minimum value is {min}', { min: detail(errors, key).min });
    case DefaultValidationError.Max:
      return translate.instant('Maximum value is {max}', { max: detail(errors, key).max });
    case DefaultValidationError.Required:
      return translate.instant('Field is required');
    case DefaultValidationError.Email:
      return translate.instant('Value must be a valid email address');
    case DefaultValidationError.Cpu:
      return translate.instant('Invalid CPU configuration.');
    case DefaultValidationError.MinLength:
      return translate.instant(
        T('The length of the field should be at least {minLength}'),
        { minLength: detail(errors, key).requiredLength },
      );
    case DefaultValidationError.MaxLength:
      return translate.instant(
        T('The length of the field should be no more than {maxLength}'),
        { maxLength: detail(errors, key).requiredLength },
      );
    case DefaultValidationError.Range:
      return translate.instant(
        'The value is out of range. Enter a value between {min} and {max}.',
        { min: detail(errors, 'rangeValue').min, max: detail(errors, 'rangeValue').max },
      );
    case DefaultValidationError.Pattern:
      return translate.instant('Invalid format or character');
    case DefaultValidationError.Forbidden:
      return translate.instant('The name "{value}" is already in use.', { value: errors.value as string });
    case DefaultValidationError.Number:
      return translate.instant('Value must be a number');
    case DefaultValidationError.Cron:
      return translate.instant('Invalid cron expression');
    case DefaultValidationError.Ip2:
      return translate.instant('Invalid IP address');
    case DefaultValidationError.InvalidRegex:
      return translate.instant('Invalid regular expression');
    case DefaultValidationError.InvalidStrftimeSpecifier:
      return translate.instant('Invalid format specifier: {specifier}', {
        specifier: detail(errors, key).specifier,
      });
    case DefaultValidationError.ContainsSlash:
      return translate.instant('Forward slashes are not allowed');
    case DefaultValidationError.InvalidCharacters:
      return translate.instant('Contains invalid characters');
    case DefaultValidationError.OrphanedPercent:
      return translate.instant('Percent sign at end must be escaped as %%');
    case DefaultValidationError.InvalidRcloneBandwidthLimit:
      return translate.instant('Invalid Rclone bandwidth limit: {value}', {
        value: detail(errors, key).value,
      });
    case DefaultValidationError.SelectionMustBeFile:
      return translate.instant('Selected path must be a file and not a directory');
    case DefaultValidationError.Empty:
      return translate.instant('Value cannot be empty or whitespace only');
    case DefaultValidationError.ExactLength:
      return translate.instant(
        T('The length must be exactly {requiredLength} (current length: {actualLength})'),
        {
          requiredLength: detail(errors, key).requiredLength,
          actualLength: detail(errors, key).actualLength,
        },
      );
    case DefaultValidationError.MinArrayLength: {
      const minLength = detail(errors, key).requiredLength as number;
      return translate.instant(
        minLength === 1
          ? T('List should have at least {minLength} item')
          : T('List should have at least {minLength} items'),
        { minLength },
      );
    }
    case DefaultValidationError.MaxArrayLength: {
      const maxLength = detail(errors, key).requiredLength as number;
      return translate.instant(
        maxLength === 1
          ? T('List should have no more than {maxLength} item')
          : T('List should have no more than {maxLength} items'),
        { maxLength },
      );
    }
    default:
      return null;
  }
}

/**
 * App-wide resolver for `tn-form-field` validation messages.
 *
 * The library ships English-only fallbacks and no project-specific validator
 * wording, so this wires its `TN_FORM_FIELD_ERRORS` hook to `TranslateService`
 * and reuses the same messages as the legacy `ix-errors` component:
 * 1. A custom validator that stored a pre-built `{ message }` string wins.
 * 2. A server-side validation error (`manualValidateError`) resolves to the
 *    message its sibling keys carry.
 * 3. Otherwise a known error key resolves to its translated message.
 * 4. Unknown keys return `null` to fall through to the library defaults.
 */
export function provideTnFormFieldErrors(): Provider {
  return {
    provide: TN_FORM_FIELD_ERRORS,
    useFactory: (): TnFormFieldErrorResolver => {
      const translate = inject(TranslateService);
      return (errorKey: string, errorValue: unknown, control: AbstractControl | null) => {
        const customMessage = (errorValue as { message?: unknown } | null)?.message;
        if (typeof customMessage === 'string' && customMessage.trim()) {
          return customMessage;
        }

        // The active error key for a server validation failure is the bare
        // `manualValidateError: true` flag; its message sits in a sibling key.
        if (manualValidateKeys.has(errorKey)) {
          const manualMessage = manualValidationMessage(control?.errors);
          if (manualMessage) {
            return manualMessage;
          }
        }

        return translateError(translate, errorKey, control?.errors ?? { [errorKey]: errorValue });
      };
    },
  };
}

/**
 * App-wide list of error keys a user may close.
 *
 * Only the manual validation keys: a server-side rejection is not something the
 * user can retype their way out of, so without a close button the message sits
 * under the field until the control changes. The legacy `ix-errors` component
 * gave every such message a close icon; wiring the keys once here keeps that
 * behaviour for every `tn-form-field` and `tn-form-errors` in the app, rather
 * than needing the input on each of hundreds of fields.
 *
 * `FormErrorHandlerService` writes all three keys together, and the resolver
 * above reads across them, so all three are listed — whichever ends up active is
 * the one carrying the message on screen.
 */
export function provideTnFormFieldDismissibleErrors(): Provider {
  return {
    provide: TN_FORM_FIELD_DISMISSIBLE_ERRORS,
    useValue: [...manualValidateKeys],
  };
}
